'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import PlatformNav from '@/components/PlatformNav';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  helpful?: number;
}

interface KBArticle {
  id: string;
  title: string;
  summary: string;
  category: string;
  readTime: string;
  icon: string;
  tags: string[];
}

interface SupportTicket {
  id: string;
  ticket_number: string;
  category: string;
  subject: string;
  description: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
  booking_id?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

type HelpView = 'home' | 'faq' | 'ai_chat' | 'tickets' | 'create_ticket' | 'knowledge_base' | 'article' | 'contact';

// ─── Data ─────────────────────────────────────────────────────────────────────
const FAQS: FAQ[] = [
  { id: 'f1', category: 'booking', question: 'How do I book a journey?', answer: 'Browse our collection at /all-journeys, select your preferred journey, choose your departure date and guest count, then proceed through our secure 6-step checkout. You can pay by card, bank transfer, or 25% deposit.' },
  { id: 'f2', category: 'booking', question: 'Can I modify my booking after confirmation?', answer: 'Yes, amendments are possible up to 60 days before departure subject to availability. Contact our concierge team or submit an amendment request through My Bookings. Amendment fees may apply.' },
  { id: 'f3', category: 'booking', question: 'What is included in my journey price?', answer: 'All WorldwayLuxe journeys include luxury accommodation, daily breakfast, guided excursions, airport transfers, and a dedicated concierge. Flights, visas, and personal expenses are typically excluded unless stated.' },
  { id: 'f4', category: 'payment', question: 'What payment methods are accepted?', answer: 'We accept all major credit/debit cards (Visa, Mastercard, Amex), UPI, net banking, and bank wire transfers. You can also pay a 25% deposit to secure your booking with the balance due 60 days before departure.' },
  { id: 'f5', category: 'payment', question: 'When is the final payment due?', answer: 'The final balance is due 60 days before your departure date. You will receive an automated reminder 75 days before departure. Late payments may result in booking cancellation.' },
  { id: 'f6', category: 'payment', question: 'How do I get my invoice and receipt?', answer: 'Your payment receipt and invoice are automatically generated after each payment. Access them from My Bookings → Invoice, or visit /payment-receipt?id=YOUR_BOOKING_ID. PDFs are available for download.' },
  { id: 'f7', category: 'cancellation', question: 'What is the cancellation policy?', answer: 'Cancellations 90+ days before departure: 10% fee. 60–89 days: 25% fee. 30–59 days: 50% fee. Under 30 days: 100% non-refundable. Travel insurance is strongly recommended.' },
  { id: 'f8', category: 'cancellation', question: 'How long does a refund take?', answer: 'Approved refunds are processed within 7–14 business days to your original payment method. Bank transfers may take an additional 3–5 business days. You will receive email confirmation when the refund is initiated.' },
  { id: 'f9', category: 'visa', question: 'Do I need a visa for my destination?', answer: 'Visa requirements vary by nationality and destination. Your journey confirmation email includes destination-specific visa guidance. We recommend checking with the relevant embassy 3 months before travel.' },
  { id: 'f10', category: 'visa', question: 'Can WorldwayLuxe assist with visa applications?', answer: 'Yes, our visa support team can provide invitation letters, hotel confirmations, and itinerary documents required for visa applications. Submit a Visa Support ticket through our Help Centre.' },
  { id: 'f11', category: 'insurance', question: 'Is travel insurance included?', answer: 'Travel insurance is not included by default but is strongly recommended. We partner with leading insurers to offer comprehensive coverage. Add insurance during checkout or contact us to add it post-booking.' },
  { id: 'f12', category: 'insurance', question: 'What does travel insurance cover?', answer: 'Our recommended travel insurance covers trip cancellation, medical emergencies, evacuation, lost baggage, and travel delays. Policy documents are provided upon purchase.' },
  { id: 'f13', category: 'account', question: 'How do I access my booking documents?', answer: 'All documents are available in your Customer Portal under My Bookings. You can download your itinerary, voucher, invoice, and receipt at any time. Documents are also emailed automatically.' },
  { id: 'f14', category: 'account', question: 'How does the loyalty programme work?', answer: 'WorldwayLuxe Rewards earns you points on every booking. Points can be redeemed for discounts, upgrades, and exclusive experiences. View your balance and tier status in the Customer Portal.' },
  { id: 'f15', category: 'journey', question: 'What is the physical activity level of journeys?', answer: 'Each journey has a clearly marked activity level: Leisurely, Moderate, Active, or Challenging. Check the journey detail page for specific requirements. We can recommend alternatives based on your fitness level.' },
];

const KB_ARTICLES: KBArticle[] = [
  { id: 'kb1', title: 'Complete Guide to Booking Your First Journey', summary: 'Step-by-step walkthrough of the booking process, from browsing to confirmation.', category: 'Getting Started', readTime: '5 min', icon: '🚀', tags: ['booking', 'first-time', 'checkout'] },
  { id: 'kb2', title: 'Understanding Your Payment Options', summary: 'Detailed breakdown of all payment methods, deposit schemes, and instalment plans.', category: 'Payments', readTime: '4 min', icon: '💳', tags: ['payment', 'deposit', 'invoice'] },
  { id: 'kb3', title: 'Cancellation & Refund Policy Explained', summary: 'Full cancellation timeline, refund calculations, and how to submit a cancellation request.', category: 'Cancellations', readTime: '6 min', icon: '❌', tags: ['cancellation', 'refund', 'policy'] },
  { id: 'kb4', title: 'Visa Requirements by Destination', summary: 'Country-by-country visa guide with application tips and required documents.', category: 'Visa & Documents', readTime: '8 min', icon: '📄', tags: ['visa', 'documents', 'passport'] },
  { id: 'kb5', title: 'Travel Insurance: What You Need to Know', summary: 'Coverage types, claim procedures, and how to add insurance to your booking.', category: 'Insurance', readTime: '5 min', icon: '🛡️', tags: ['insurance', 'coverage', 'claims'] },
  { id: 'kb6', title: 'WorldwayLuxe Rewards Programme Guide', summary: 'How to earn points, tier benefits, and redeeming rewards for upgrades and discounts.', category: 'Loyalty', readTime: '4 min', icon: '⭐', tags: ['loyalty', 'rewards', 'points'] },
  { id: 'kb7', title: 'Packing Guide for Luxury Journeys', summary: 'Essential packing tips, luggage allowances, and destination-specific recommendations.', category: 'Travel Tips', readTime: '7 min', icon: '🧳', tags: ['packing', 'luggage', 'tips'] },
  { id: 'kb8', title: 'How to Amend or Reschedule Your Booking', summary: 'Amendment process, fees, and how to request date changes or guest modifications.', category: 'Amendments', readTime: '4 min', icon: '✏️', tags: ['amendment', 'reschedule', 'modify'] },
  { id: 'kb9', title: 'Accessing Your Travel Documents', summary: 'Where to find your itinerary, voucher, invoice, and receipt in the Customer Portal.', category: 'Documents', readTime: '3 min', icon: '📁', tags: ['documents', 'voucher', 'receipt'] },
];

const CATEGORIES = [
  { id: 'all', label: 'All Topics', icon: '🌐' },
  { id: 'booking', label: 'Booking', icon: '📋' },
  { id: 'payment', label: 'Payments', icon: '💳' },
  { id: 'cancellation', label: 'Cancellations', icon: '❌' },
  { id: 'visa', label: 'Visa & Docs', icon: '📄' },
  { id: 'insurance', label: 'Insurance', icon: '🛡️' },
  { id: 'account', label: 'Account', icon: '👤' },
  { id: 'journey', label: 'Journeys', icon: '✈️' },
];

const TICKET_CATEGORIES = [
  { id: 'general_enquiry', label: 'General Enquiry', icon: '💬' },
  { id: 'booking_help', label: 'Booking Help', icon: '📋' },
  { id: 'cancellation_request', label: 'Cancellation Request', icon: '❌' },
  { id: 'amendment_request', label: 'Amendment Request', icon: '✏️' },
  { id: 'refund_request', label: 'Refund Request', icon: '💰' },
  { id: 'payment_issue', label: 'Payment Issue', icon: '💳' },
  { id: 'visa_support', label: 'Visa Support', icon: '📄' },
  { id: 'document_request', label: 'Document Request', icon: '📁' },
  { id: 'complaint', label: 'Complaint', icon: '⚠️' },
  { id: 'other', label: 'Other', icon: '❓' },
];

// ─── AI Concierge Chat ─────────────────────────────────────────────────────────
function AIConciergeChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: "Hello! I'm your WorldwayLuxe AI Concierge. I can help you with booking questions, visa requirements, travel tips, cancellation policies, and anything else about your journey. How can I assist you today?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === response) return prev;
        return [...prev, assistantMsg];
      });
      setConversationHistory((prev) => [...prev, { role: 'assistant', content: response }]);
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    const userText = input.trim();
    setInput('');

    const userMsg: ChatMessage = { role: 'user', content: userText, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);

    const newHistory = [...conversationHistory, { role: 'user', content: userText }];
    setConversationHistory(newHistory);

    const apiMessages = [
      {
        role: 'system',
        content: `You are the WorldwayLuxe AI Concierge — a knowledgeable, warm, and professional luxury travel assistant for Worldway × Abercrombie & Kent. 
        Help customers with: booking questions, visa requirements, cancellation policies, travel tips, packing advice, destination information, payment queries, and general travel support.
        Be concise (2-4 sentences), friendly, and specific. Always offer to connect them with a human agent for complex issues.
        Key policies: Cancellations 90+ days = 10% fee, 60-89 days = 25%, 30-59 days = 50%, under 30 days = 100% non-refundable.
        Final payment due 60 days before departure. Support: support@worldwayluxe.com, +1 (800) WORLDWAY.`,
      },
      ...newHistory,
    ];

    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 400 });
  };

  const QUICK_QUESTIONS = [
    'What is your cancellation policy?',
    'How do I get my invoice?',
    'Do I need travel insurance?',
    'How do I modify my booking?',
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div
        className="px-5 py-4 flex items-center gap-3 border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-sm"
          style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}
        >
          ✨
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>AI Concierge</p>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Online · Powered by Gemini</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs mr-2 shrink-0 mt-0.5"
                style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
              >
                ✨
              </div>
            )}
            <div
              className="max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed"
              style={{
                background: msg.role === 'user' ? 'var(--primary)' : 'var(--surface-3)',
                color: msg.role === 'user' ? 'var(--primary-foreground)' : 'var(--foreground)',
                borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs mr-2 shrink-0 mt-0.5"
              style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
            >
              ✨
            </div>
            <div className="px-3.5 py-2.5 rounded-2xl" style={{ background: 'var(--surface-3)' }}>
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full animate-bounce"
                    style={{ background: 'var(--muted-foreground)', animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions */}
      {messages.length === 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {QUICK_QUESTIONS.map((q) => (
            <button
              key={q}
              onClick={() => { setInput(q); }}
              className="text-xs px-3 py-1.5 rounded-full border transition-all hover:border-[rgba(201,168,76,0.4)]"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', background: 'var(--surface-2)' }}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <div
          className="flex gap-2 p-2 rounded-xl border"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Ask anything about your journey..."
            className="flex-1 bg-transparent text-sm focus:outline-none px-2"
            style={{ color: 'var(--foreground)' }}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:opacity-40"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            <Icon name="PaperAirplaneIcon" size={14} />
          </button>
        </div>
        <p className="text-xs text-center mt-2" style={{ color: 'var(--muted-foreground)' }}>
          AI responses are for guidance only. For urgent issues, contact our team directly.
        </p>
      </div>
    </div>
  );
}

// ─── FAQ Section ──────────────────────────────────────────────────────────────
function FAQSection({ searchQuery }: { searchQuery: string }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, boolean>>({});

  const filtered = FAQS.filter((faq) => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = !searchQuery || faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border"
            style={{
              background: activeCategory === cat.id ? 'var(--primary)' : 'var(--surface-2)',
              color: activeCategory === cat.id ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
              borderColor: activeCategory === cat.id ? 'var(--primary)' : 'var(--border)',
            }}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* FAQ Items */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-4xl mb-3">🔍</p>
            <p className="font-semibold" style={{ color: 'var(--foreground)' }}>No results found</p>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>Try a different search term or category</p>
          </div>
        ) : (
          filtered.map((faq) => (
            <div
              key={faq.id}
              className="rounded-xl overflow-hidden border transition-all"
              style={{
                borderColor: openFaq === faq.id ? 'rgba(201,168,76,0.3)' : 'var(--border)',
                background: 'var(--surface-2)',
              }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="text-sm font-medium pr-4" style={{ color: 'var(--foreground)' }}>
                  {faq.question}
                </span>
                <Icon
                  name={openFaq === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={16}
                  style={{ color: 'var(--muted-foreground)', flexShrink: 0 }}
                />
              </button>
              {openFaq === faq.id && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    {faq.answer}
                  </p>
                  <div className="flex items-center gap-3 mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Was this helpful?</span>
                    <button
                      onClick={() => setHelpfulVotes((v) => ({ ...v, [faq.id]: true }))}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg transition-all border"
                      style={{
                        borderColor: helpfulVotes[faq.id] === true ? 'rgba(16,185,129,0.4)' : 'var(--border)',
                        color: helpfulVotes[faq.id] === true ? '#10b981' : 'var(--muted-foreground)',
                        background: helpfulVotes[faq.id] === true ? 'rgba(16,185,129,0.08)' : 'transparent',
                      }}
                    >
                      👍 Yes
                    </button>
                    <button
                      onClick={() => setHelpfulVotes((v) => ({ ...v, [faq.id]: false }))}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg transition-all border"
                      style={{
                        borderColor: helpfulVotes[faq.id] === false ? 'rgba(239,68,68,0.4)' : 'var(--border)',
                        color: helpfulVotes[faq.id] === false ? '#ef4444' : 'var(--muted-foreground)',
                        background: helpfulVotes[faq.id] === false ? 'rgba(239,68,68,0.08)' : 'transparent',
                      }}
                    >
                      👎 No
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Knowledge Base ───────────────────────────────────────────────────────────
function KnowledgeBase({ searchQuery }: { searchQuery: string }) {
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);

  const filtered = KB_ARTICLES.filter((a) =>
    !searchQuery ||
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.tags.some((t) => t.includes(searchQuery.toLowerCase()))
  );

  if (selectedArticle) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelectedArticle(null)}
          className="flex items-center gap-2 text-sm transition-colors"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <Icon name="ArrowLeftIcon" size={14} />
          Back to Knowledge Base
        </button>
        <div
          className="rounded-2xl p-6"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-start gap-4 mb-6">
            <span className="text-3xl">{selectedArticle.icon}</span>
            <div>
              <span
                className="text-xs px-2 py-0.5 rounded-full mb-2 inline-block"
                style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--primary)' }}
              >
                {selectedArticle.category}
              </span>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                {selectedArticle.title}
              </h2>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                {selectedArticle.readTime} read
              </p>
            </div>
          </div>
          <div className="prose prose-sm max-w-none" style={{ color: 'var(--foreground)' }}>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
              {selectedArticle.summary}
            </p>
            <div className="space-y-4 text-sm" style={{ color: 'var(--foreground)' }}>
              <p>This comprehensive guide covers everything you need to know about {selectedArticle.title.toLowerCase()}. Our WorldwayLuxe team has compiled the most important information to ensure your luxury travel experience is seamless from start to finish.</p>
              <p>For personalised assistance, our AI Concierge is available 24/7 to answer specific questions about your booking. You can also reach our human concierge team via the contact options below.</p>
              <div
                className="p-4 rounded-xl"
                style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}
              >
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--primary)' }}>💡 Pro Tip</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  For the most up-to-date information specific to your booking, always check your Customer Portal or contact our concierge team directly.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-6">
            {selectedArticle.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2.5 py-1 rounded-full"
                style={{ background: 'var(--surface-3)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-4xl mb-3">📚</p>
          <p className="font-semibold" style={{ color: 'var(--foreground)' }}>No articles found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>Try a different search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((article) => (
            <button
              key={article.id}
              onClick={() => setSelectedArticle(article)}
              className="text-left p-5 rounded-2xl border transition-all hover:border-[rgba(201,168,76,0.3)] group"
              style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">{article.icon}</span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(201,168,76,0.08)', color: 'var(--primary)' }}
                >
                  {article.category}
                </span>
              </div>
              <h3 className="text-sm font-semibold mb-2 group-hover:text-[var(--primary)] transition-colors" style={{ color: 'var(--foreground)' }}>
                {article.title}
              </h3>
              <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--muted-foreground)' }}>
                {article.summary}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {article.readTime} read
                </span>
                <Icon name="ArrowRightIcon" size={12} style={{ color: 'var(--primary)' }} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Support Tickets ──────────────────────────────────────────────────────────
function SupportTickets({ onCreateTicket }: { onCreateTicket: () => void }) {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);

  const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
    open: { label: 'Open', color: '#3b82f6' },
    pending: { label: 'Pending', color: '#f59e0b' },
    in_progress: { label: 'In Progress', color: '#8b5cf6' },
    waiting_customer: { label: 'Awaiting You', color: '#f97316' },
    resolved: { label: 'Resolved', color: '#10b981' },
    closed: { label: 'Closed', color: '#64748b' },
    escalated: { label: 'Escalated', color: '#ef4444' },
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/support/tickets?limit=20');
      const data = await res.json();
      if (data?.data) setTickets(data.data);
    } catch {
      // handled by empty state
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-xl animate-pulse" style={{ background: 'var(--surface-2)' }} />
        ))}
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-5xl mb-4">🎫</p>
        <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
          No Support Tickets
        </h3>
        <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
          Need help? Create a support ticket and our team will assist you within 2 hours.
        </p>
        <button
          onClick={onCreateTicket}
          className="px-8 py-3 rounded-xl text-sm font-semibold transition-all"
          style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          Create Support Ticket
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tickets.map((ticket) => {
        const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
        return (
          <Link
            key={ticket.id}
            href="/customer-support"
            className="block p-5 rounded-xl border transition-all hover:border-[rgba(201,168,76,0.3)]"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                    {ticket.ticket_number}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: `${statusCfg.color}20`, color: statusCfg.color }}
                  >
                    {statusCfg.label}
                  </span>
                </div>
                <p className="text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>
                  {ticket.subject}
                </p>
                <p className="text-xs mt-0.5 capitalize" style={{ color: 'var(--muted-foreground)' }}>
                  {ticket.category.replace(/_/g, ' ')}
                </p>
              </div>
              <p className="text-xs shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                {new Date(ticket.created_at).toLocaleDateString()}
              </p>
            </div>
          </Link>
        );
      })}
      <div className="text-center pt-2">
        <Link href="/customer-support" className="text-sm hover:underline" style={{ color: 'var(--primary)' }}>
          View all tickets in Support Portal →
        </Link>
      </div>
    </div>
  );
}

// ─── Create Ticket Form ───────────────────────────────────────────────────────
function CreateTicketForm({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState({
    category: 'general_enquiry',
    subject: '',
    description: '',
    priority: 'medium',
    booking_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketRef, setTicketRef] = useState('');

  const handleSubmit = async () => {
    if (!form.subject || !form.description) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data?.data) {
        setTicketRef(data.data.ticket_number || 'TKT-' + Math.random().toString(36).slice(2, 8).toUpperCase());
        setSubmitted(true);
      }
    } catch {
      toast.error('Failed to submit ticket. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-2xl mx-auto mb-4"
          style={{ background: 'rgba(16,185,129,0.1)', border: '2px solid rgba(16,185,129,0.3)' }}
        >
          ✅
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
          Ticket Submitted!
        </h3>
        <p className="text-sm mb-2" style={{ color: 'var(--muted-foreground)' }}>
          Your support ticket has been created successfully.
        </p>
        <p className="text-sm font-mono font-bold mb-6" style={{ color: 'var(--primary)' }}>
          {ticketRef}
        </p>
        <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>
          Our team will respond within 2 hours. You will receive an email confirmation shortly.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={onBack}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border transition-all"
            style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--surface-2)' }}
          >
            Back to Help Centre
          </button>
          <Link
            href="/customer-support"
            className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            View My Tickets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} style={{ color: 'var(--muted-foreground)' }}>
          <Icon name="ArrowLeftIcon" size={18} />
        </button>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
          New Support Ticket
        </h2>
      </div>

      {/* Category */}
      <div className="rounded-2xl p-5" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
        <label className="block text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Category
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TICKET_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setForm((f) => ({ ...f, category: cat.id }))}
              className="flex items-center gap-2 p-3 rounded-xl text-xs font-medium transition-all border text-left"
              style={{
                borderColor: form.category === cat.id ? 'var(--primary)' : 'var(--border)',
                background: form.category === cat.id ? 'rgba(201,168,76,0.08)' : 'transparent',
                color: form.category === cat.id ? 'var(--primary)' : 'var(--muted-foreground)',
              }}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Subject & Description */}
      <div className="rounded-2xl p-5 space-y-4" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
            Subject *
          </label>
          <input
            type="text"
            placeholder="Brief description of your issue"
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none border transition-all"
            style={{
              background: 'var(--surface-3)',
              borderColor: 'var(--border)',
              color: 'var(--foreground)',
            }}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
            Description *
          </label>
          <textarea
            placeholder="Please describe your issue in detail..."
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={5}
            className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none border transition-all resize-none"
            style={{
              background: 'var(--surface-3)',
              borderColor: 'var(--border)',
              color: 'var(--foreground)',
            }}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
            Booking Reference (optional)
          </label>
          <input
            type="text"
            placeholder="e.g. WL-2024-001234"
            value={form.booking_id}
            onChange={(e) => setForm((f) => ({ ...f, booking_id: e.target.value }))}
            className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none border transition-all"
            style={{
              background: 'var(--surface-3)',
              borderColor: 'var(--border)',
              color: 'var(--foreground)',
            }}
          />
        </div>
      </div>

      {/* Priority */}
      <div className="rounded-2xl p-5" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
        <label className="block text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Priority
        </label>
        <div className="flex gap-2">
          {[
            { id: 'low', label: 'Low', color: '#64748b' },
            { id: 'medium', label: 'Medium', color: '#f59e0b' },
            { id: 'high', label: 'High', color: '#ef4444' },
            { id: 'urgent', label: 'Urgent', color: '#dc2626' },
          ].map((p) => (
            <button
              key={p.id}
              onClick={() => setForm((f) => ({ ...f, priority: p.id }))}
              className="flex-1 py-2 rounded-lg text-xs font-medium transition-all border"
              style={
                form.priority === p.id
                  ? { color: p.color, background: `${p.color}15`, borderColor: p.color }
                  : { color: 'var(--muted-foreground)', borderColor: 'var(--border)', background: 'transparent' }
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-8 py-3 rounded-xl text-sm font-semibold border transition-all"
          style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--surface-2)' }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !form.subject || !form.description}
          className="px-10 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-40"
          style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          {submitting ? 'Submitting...' : 'Submit Ticket'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function HelpSupportCenterContent() {
  const { user } = useAuth();
  const [view, setView] = useState<HelpView>('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    if (searchInput.trim()) {
      setView('faq');
    }
  };

  const STATS = [
    { label: 'Avg. Response Time', value: '< 2 hrs', icon: '⚡' },
    { label: 'Customer Satisfaction', value: '98.4%', icon: '⭐' },
    { label: 'Issues Resolved', value: '24/7', icon: '✅' },
    { label: 'Support Languages', value: '12+', icon: '🌐' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <PlatformNav />
      <div className="pt-16">
        {/* Hero Header */}
        <div
          className="border-b"
          style={{
            borderColor: 'var(--border)',
            background: 'linear-gradient(135deg, var(--surface-2) 0%, rgba(201,168,76,0.03) 100%)',
          }}
        >
          <div className="max-w-6xl mx-auto px-6 py-10">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: 'var(--primary)' }}>
                WorldwayLuxe
              </p>
              <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                Help &amp; Support Centre
              </h1>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Find answers instantly, chat with our AI Concierge, or connect with our expert team — available 24/7.
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-8">
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all"
                style={{ background: 'var(--surface-2)', borderColor: 'rgba(201,168,76,0.2)' }}
              >
                <Icon name="MagnifyingGlassIcon" size={18} style={{ color: 'var(--muted-foreground)' }} />
                <input
                  type="text"
                  placeholder="Search FAQs, articles, topics..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                  style={{ color: 'var(--foreground)' }}
                />
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-xl text-xs font-semibold transition-all"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  Search
                </button>
              </div>
            </form>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              {STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-lg mb-0.5">{stat.icon}</p>
                  <p className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{stat.value}</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex gap-1 overflow-x-auto pb-px">
              {[
                { id: 'home', label: 'Overview', icon: 'HomeIcon' },
                { id: 'faq', label: 'FAQs', icon: 'QuestionMarkCircleIcon' },
                { id: 'ai_chat', label: 'AI Concierge', icon: 'SparklesIcon' },
                { id: 'knowledge_base', label: 'Knowledge Base', icon: 'BookOpenIcon' },
                { id: 'tickets', label: 'My Tickets', icon: 'TicketIcon' },
                { id: 'contact', label: 'Contact Us', icon: 'PhoneIcon' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setView(tab.id as HelpView)}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap -mb-px"
                  style={{
                    borderColor: view === tab.id ? 'var(--primary)' : 'transparent',
                    color: view === tab.id ? 'var(--primary)' : 'var(--muted-foreground)',
                  }}
                >
                  <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* ── Home / Overview ── */}
          {view === 'home' && (
            <div className="space-y-8">
              {/* Quick Actions */}
              <div>
                <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                  How can we help you?
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { icon: '✨', title: 'AI Concierge', desc: 'Get instant answers from our AI travel assistant', action: () => setView('ai_chat'), badge: 'Instant' },
                    { icon: '🎫', title: 'Submit a Ticket', desc: 'Create a support request for complex issues', action: () => setView('create_ticket'), badge: '< 2hr response' },
                    { icon: '❓', title: 'Browse FAQs', desc: 'Find answers to common questions', action: () => setView('faq'), badge: `${FAQS.length} articles` },
                    { icon: '📚', title: 'Knowledge Base', desc: 'In-depth guides and how-to articles', action: () => setView('knowledge_base'), badge: `${KB_ARTICLES.length} guides` },
                    { icon: '📋', title: 'My Tickets', desc: 'Track your open support requests', action: () => setView('tickets'), badge: 'Live status' },
                    { icon: '📞', title: 'Contact Us', desc: 'Phone, email, and live chat options', action: () => setView('contact'), badge: '24/7' },
                  ].map((item) => (
                    <button
                      key={item.title}
                      onClick={item.action}
                      className="text-left p-5 rounded-2xl border transition-all hover:border-[rgba(201,168,76,0.3)] group"
                      style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-2xl">{item.icon}</span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(201,168,76,0.08)', color: 'var(--primary)' }}
                        >
                          {item.badge}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ color: 'var(--foreground)' }}>
                        {item.title}
                      </h3>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular FAQs */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                    Popular Questions
                  </h2>
                  <button onClick={() => setView('faq')} className="text-xs hover:underline" style={{ color: 'var(--primary)' }}>
                    View all FAQs →
                  </button>
                </div>
                <div className="space-y-2">
                  {FAQS.slice(0, 5).map((faq) => (
                    <button
                      key={faq.id}
                      onClick={() => setView('faq')}
                      className="w-full text-left flex items-center justify-between p-4 rounded-xl border transition-all hover:border-[rgba(201,168,76,0.3)]"
                      style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                    >
                      <span className="text-sm" style={{ color: 'var(--foreground)' }}>{faq.question}</span>
                      <Icon name="ChevronRightIcon" size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Booking Assistance */}
              <div
                className="rounded-2xl p-6"
                style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.06), rgba(201,168,76,0.02))', border: '1px solid rgba(201,168,76,0.15)' }}
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                      Need Booking Assistance?
                    </h3>
                    <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
                      Our expert concierge team can help you find the perfect journey, customise your itinerary, or resolve any booking issues.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Link
                        href="/all-journeys"
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                        style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                      >
                        <Icon name="GlobeAltIcon" size={12} />
                        Browse Journeys
                      </Link>
                      <button
                        onClick={() => setView('ai_chat')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all border"
                        style={{ borderColor: 'rgba(201,168,76,0.3)', color: 'var(--primary)', background: 'transparent' }}
                      >
                        <Icon name="SparklesIcon" size={12} />
                        Ask AI Concierge
                      </button>
                    </div>
                  </div>
                  <div className="hidden md:block text-5xl">✈️</div>
                </div>
              </div>
            </div>
          )}

          {/* ── FAQ ── */}
          {view === 'faq' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                  Frequently Asked Questions
                </h2>
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchInput(''); }}
                    className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg border"
                    style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', background: 'var(--surface-2)' }}
                  >
                    <Icon name="XMarkIcon" size={12} />
                    Clear search: &quot;{searchQuery}&quot;
                  </button>
                )}
              </div>
              <FAQSection searchQuery={searchQuery} />
            </div>
          )}

          {/* ── AI Concierge Chat ── */}
          {view === 'ai_chat' && (
            <div className="max-w-2xl mx-auto">
              <div className="mb-4">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                  AI Concierge
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Powered by Gemini · Available 24/7 · Instant responses
                </p>
              </div>
              <div
                className="rounded-2xl overflow-hidden flex flex-col"
                style={{ height: '600px', border: '1px solid var(--border)', background: 'var(--surface-2)' }}
              >
                <AIConciergeChat />
              </div>
              <div
                className="mt-4 p-4 rounded-xl flex items-start gap-3"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
              >
                <Icon name="InformationCircleIcon" size={16} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  The AI Concierge provides general guidance. For account-specific issues, booking changes, or urgent matters, please{' '}
                  <button onClick={() => setView('create_ticket')} className="underline" style={{ color: 'var(--primary)' }}>
                    submit a support ticket
                  </button>{' '}
                  or{' '}
                  <button onClick={() => setView('contact')} className="underline" style={{ color: 'var(--primary)' }}>
                    contact our team directly
                  </button>.
                </p>
              </div>
            </div>
          )}

          {/* ── Knowledge Base ── */}
          {view === 'knowledge_base' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                  Knowledge Base
                </h2>
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchInput(''); }}
                    className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg border"
                    style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', background: 'var(--surface-2)' }}
                  >
                    <Icon name="XMarkIcon" size={12} />
                    Clear: &quot;{searchQuery}&quot;
                  </button>
                )}
              </div>
              <KnowledgeBase searchQuery={searchQuery} />
            </div>
          )}

          {/* ── My Tickets ── */}
          {view === 'tickets' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                  My Support Tickets
                </h2>
                <button
                  onClick={() => setView('create_ticket')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <Icon name="PlusIcon" size={14} />
                  New Ticket
                </button>
              </div>
              <SupportTickets onCreateTicket={() => setView('create_ticket')} />
            </div>
          )}

          {/* ── Create Ticket ── */}
          {view === 'create_ticket' && (
            <CreateTicketForm onBack={() => setView('tickets')} />
          )}

          {/* ── Contact Us ── */}
          {view === 'contact' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}>
                Contact Us
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    icon: '📞',
                    title: '24/7 Concierge Hotline',
                    detail: '+1 (800) WORLDWAY',
                    sub: 'Available around the clock',
                    action: 'tel:+18009675392',
                    actionLabel: 'Call Now',
                    badge: 'Instant',
                  },
                  {
                    icon: '📧',
                    title: 'Email Support',
                    detail: 'support@worldwayluxe.com',
                    sub: 'Response within 2 hours',
                    action: 'mailto:support@worldwayluxe.com',
                    actionLabel: 'Send Email',
                    badge: '< 2hr',
                  },
                  {
                    icon: '💬',
                    title: 'Live Chat',
                    detail: 'Chat with a human agent',
                    sub: 'Mon–Fri 9am–9pm GMT',
                    action: null,
                    actionLabel: 'Start Chat',
                    badge: 'Business hours',
                  },
                  {
                    icon: '✈️',
                    title: 'Booking Enquiries',
                    detail: 'bookings@worldwayluxe.com',
                    sub: 'New bookings & amendments',
                    action: 'mailto:bookings@worldwayluxe.com',
                    actionLabel: 'Email Bookings',
                    badge: 'Sales',
                  },
                  {
                    icon: '🆘',
                    title: 'Emergency Line',
                    detail: '+1 (800) WL-URGENT',
                    sub: 'For travellers in distress',
                    action: 'tel:+18009587436',
                    actionLabel: 'Emergency Call',
                    badge: '24/7 Priority',
                  },
                  {
                    icon: '🏢',
                    title: 'Head Office',
                    detail: 'London · Dubai · Singapore',
                    sub: 'Global offices worldwide',
                    action: null,
                    actionLabel: 'View Offices',
                    badge: 'In-person',
                  },
                ].map((contact) => (
                  <div
                    key={contact.title}
                    className="p-5 rounded-2xl border"
                    style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-2xl">{contact.icon}</span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(201,168,76,0.08)', color: 'var(--primary)' }}
                      >
                        {contact.badge}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
                      {contact.title}
                    </h3>
                    <p className="text-sm font-medium mb-0.5" style={{ color: 'var(--primary)' }}>
                      {contact.detail}
                    </p>
                    <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
                      {contact.sub}
                    </p>
                    {contact.action ? (
                      <a
                        href={contact.action}
                        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs font-semibold transition-all border"
                        style={{ borderColor: 'rgba(201,168,76,0.3)', color: 'var(--primary)', background: 'rgba(201,168,76,0.05)' }}
                      >
                        {contact.actionLabel}
                      </a>
                    ) : (
                      <button
                        onClick={() => setView('create_ticket')}
                        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs font-semibold transition-all border"
                        style={{ borderColor: 'rgba(201,168,76,0.3)', color: 'var(--primary)', background: 'rgba(201,168,76,0.05)' }}
                      >
                        {contact.actionLabel}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* SLA Commitments */}
              <div
                className="rounded-2xl p-6"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
              >
                <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--foreground)' }}>
                  Our Response Commitments
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { priority: 'Critical', sla: '30 min', color: '#dc2626' },
                    { priority: 'Urgent', sla: '1 hour', color: '#ef4444' },
                    { priority: 'High', sla: '4 hours', color: '#f59e0b' },
                    { priority: 'Standard', sla: '24 hours', color: '#64748b' },
                  ].map((item) => (
                    <div
                      key={item.priority}
                      className="p-3 rounded-xl text-center"
                      style={{ background: `${item.color}08`, border: `1px solid ${item.color}20` }}
                    >
                      <p className="text-xs font-semibold mb-1" style={{ color: item.color }}>
                        {item.priority}
                      </p>
                      <p className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>{item.sla}</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>response</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
